/**
 * The grid's small inline number boxes (doors, cases, units, revenue, per-store rate, case cost):
 * what a key does, and what a typed value means.
 *
 * Two rules, both from 1st Phorm (Bakhrom, 10/29):
 * - **Only Enter or Tab commits.** Clicking out of the box puts the old value back — a stray click
 *   used to save whatever was half-typed.
 * - **A value is taken as typed or refused out loud.** `parseInt` saved 48.7805 cases as 48, and a
 *   typed -5 landed as 0; neither said anything. Now decimals are kept (to the four places the
 *   server stores) and a negative, or a fraction of a door, is refused with a message under the box.
 */

/** What a key pressed in an inline box does. */
export type InlineEditKeyAction = "commit" | "cancel" | null

export function inline_edit_key_action(key: string): InlineEditKeyAction {
  if (key === "Enter" || key === "Tab") return "commit"
  if (key === "Escape") return "cancel"
  return null
}

/** The measures an inline box edits. */
export type CellInputField = "store_count" | "cases_sold" | "units_sold" | "volume_dollars" | "velocity" | "case_cost"

export type ParsedCellValue = { ok: true; value: number } | { ok: false; message: string }

/** Decimal places a quantity keeps: `forecast_sales_data_points.cases_decimal` is stored to four. */
const QUANTITY_DECIMALS = 4

const FIELD_LABEL: Record<CellInputField, string> = {
  store_count: "Doors",
  cases_sold: "Cases",
  units_sold: "Units",
  volume_dollars: "Revenue",
  velocity: "The per-store rate",
  case_cost: "Price per case",
}

/**
 * Read what was typed into an inline box. An emptied box means 0 (as it always has); anything
 * that is not a number, any negative, and a fractional door count are refused with a sentence the
 * box shows — the value is never quietly changed into something else.
 */
export function parse_cell_input(raw: string, field: CellInputField): ParsedCellValue {
  const text = raw.trim().replace(/,/g, "")
  if (text === "") return { ok: true, value: 0 }
  const value = Number(text)
  if (!Number.isFinite(value)) return { ok: false, message: `"${raw.trim()}" is not a number.` }
  if (value < 0) {
    return { ok: false, message: `${FIELD_LABEL[field]} can't be negative. Enter 0 or more, or press Esc to keep the old value.` }
  }
  if (field === "store_count") {
    if (!Number.isInteger(value)) return { ok: false, message: "Doors are whole stores. Enter a whole number." }
    return { ok: true, value }
  }
  if (field === "volume_dollars" || field === "case_cost") {
    return { ok: true, value: Math.round(value * 100) / 100 }
  }
  const scale = Math.pow(10, QUANTITY_DECIMALS)
  return { ok: true, value: Math.round(value * scale) / scale }
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("inline_edit_key_action", () => {
    it("commits on Enter and Tab, reverts on Escape, ignores the rest", () => {
      expect(inline_edit_key_action("Enter")).toBe("commit")
      expect(inline_edit_key_action("Tab")).toBe("commit")
      expect(inline_edit_key_action("Escape")).toBe("cancel")
      expect(inline_edit_key_action("a")).toBeNull()
      expect(inline_edit_key_action("ArrowDown")).toBeNull()
    })
  })

  describe("parse_cell_input", () => {
    it("keeps the decimals typed into cases (48.7805 used to save as 48)", () => {
      expect(parse_cell_input("48.7805", "cases_sold")).toEqual({ ok: true, value: 48.7805 })
      expect(parse_cell_input("48.7", "cases_sold")).toEqual({ ok: true, value: 48.7 })
      expect(parse_cell_input("2.5", "units_sold")).toEqual({ ok: true, value: 2.5 })
      expect(parse_cell_input("1,250.5", "cases_sold")).toEqual({ ok: true, value: 1250.5 })
    })

    it("refuses a negative with a message instead of saving 0", () => {
      const result = parse_cell_input("-5", "cases_sold")
      expect(result.ok).toBe(false)
      if (!result.ok) expect(result.message).toContain("Cases can't be negative")
      expect(parse_cell_input("-1", "store_count").ok).toBe(false)
      expect(parse_cell_input("-100", "volume_dollars").ok).toBe(false)
    })

    it("refuses a fraction of a door rather than truncating it", () => {
      expect(parse_cell_input("7.5", "store_count")).toEqual({ ok: false, message: "Doors are whole stores. Enter a whole number." })
      expect(parse_cell_input("7", "store_count")).toEqual({ ok: true, value: 7 })
    })

    it("reads an emptied box as 0 and refuses text", () => {
      expect(parse_cell_input("", "cases_sold")).toEqual({ ok: true, value: 0 })
      expect(parse_cell_input("abc", "cases_sold").ok).toBe(false)
    })

    it("rounds revenue to the cent", () => {
      expect(parse_cell_input("100.456", "volume_dollars")).toEqual({ ok: true, value: 100.46 })
    })
  })
}
